import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { useParams } from 'react-router';
import { useInterval, useMount } from 'react-use';

import { DateRange } from '@mui/x-date-pickers-pro';

import delay from 'lodash/delay';
import isEqual from 'lodash/isEqual';

import { TransportKind } from '@/graphql';
import useIsDocumentVisible from '@/hooks/useIsDocumentVisible';
import useSyncState from '@/hooks/useSyncState';
import useWebsocketGrpcMetricsEnabled from '@/hooks/useWebsocketGrpcMetricsEnabled';
import { useTrainingJobForLogsAndMetrics } from '@/sections/Training/hooks';
import { convertToDate, convertToMoment } from '@/utils/date';
import { useQuery } from '@apollo/client';
import { Moment } from 'moment';
import {
  SetterOrUpdater,
  useRecoilState, // eslint-disable-next-line camelcase
  useRecoilTransaction_UNSTABLE,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';

import { MatchParams } from '../types';
import {
  loadingNewRelativeTimeRangeCountState,
  metricsConfigState,
  relativeTimeRangeState,
  selectedMetricsUnitsState,
  sharedEndpointMetricsConfigState,
  timeRangeState,
  trainingJobMetricsConfigState,
} from './atoms';
import { getStepSecondsForTimeRange, getTimeDivisorSecondsForTimeRange } from './constants';
import {
  MetricQueryHookAPI,
  QueryVariables,
  QueryVariablesSharedEndpoint,
  QueryVariablesTrainingJob,
  RelativeTimeRange,
  UseMetricQueryArgs,
  UseQueryVariablesArgs,
} from './types';
import {
  getAbsoluteTimeRangeFromRelative,
  getAvailableMetricsConfig,
  getPollingInterval,
  validateMetricsConfig,
} from './utils';

export const EntityIdContext = createContext<string | null>(null);

export function useEntityId() {
  return useContext(EntityIdContext);
}

/**
 * Use a comparator function to determine whether to update the value or keep it the same.
 * This is particularly useful for preventing re-renders due to changes in the reference but
 * not the structure of state.
 */
export const setIfChanged =
  <T>(value: T, comparator = isEqual) =>
  (prev: T | undefined): T =>
    comparator(prev, value) ? prev : value;

export function useUpdateTimeRange() {
  const relativeTimeRange = useRecoilValue(relativeTimeRangeState);
  const setAbsoluteTimeRange = useSetRecoilState(timeRangeState);
  const isDocumentVisible = useIsDocumentVisible();

  useEffect(() => {
    if (isDocumentVisible && relativeTimeRange) {
      setAbsoluteTimeRange(setIfChanged(getAbsoluteTimeRangeFromRelative(relativeTimeRange)));
    }
  }, [isDocumentVisible, relativeTimeRange, setAbsoluteTimeRange]);

  useInterval(
    () => {
      if (relativeTimeRange) {
        setAbsoluteTimeRange(setIfChanged(getAbsoluteTimeRangeFromRelative(relativeTimeRange)));
      }
    },
    isDocumentVisible && relativeTimeRange ? getPollingInterval(relativeTimeRange) : undefined,
  );
}

export function useSetRelativeTimeRange() {
  return useRecoilTransaction_UNSTABLE(({ set }) => (relativeTimeRange: RelativeTimeRange) => {
    set(relativeTimeRangeState, relativeTimeRange);
    if (relativeTimeRange === null) {
      set(
        timeRangeState,
        setIfChanged(getAbsoluteTimeRangeFromRelative(RelativeTimeRange.LAST_1_HOUR)),
      );
    } else {
      set(timeRangeState, setIfChanged(getAbsoluteTimeRangeFromRelative(relativeTimeRange)));
    }
  });
}

function getIdQueryVariable(
  id: string,
  { isSharedEndpoint, isTrainingJob }: { isSharedEndpoint: boolean; isTrainingJob: boolean },
) {
  if (isSharedEndpoint) {
    return {
      orgSharedEndpointId: id,
    };
  }

  if (isTrainingJob) {
    return {
      trainingJobId: id,
    };
  }

  return {
    modelVersionId: id,
  };
}

function useQueryVariables({
  id,
  isSharedEndpoint,
  isTrainingJob,
  timeDivisorSeconds,
}: UseQueryVariablesArgs) {
  const [start, end] = useRecoilValue(timeRangeState);
  const { trainingJob } = useTrainingJobForLogsAndMetrics(id, !isTrainingJob);

  // TODO(Tyron): Make the backend Training metrics
  // query more robust against older start dates and
  // get rid of this hack that's only for Training.
  const cappedStart = useMemo(() => {
    if (!trainingJob?.created) {
      return start;
    }

    return new Date(Math.max(start.getTime(), new Date(trainingJob.created).getTime()));
  }, [start, trainingJob]);

  return useMemo((): QueryVariables | QueryVariablesSharedEndpoint | QueryVariablesTrainingJob => {
    return {
      ...getIdQueryVariable(id, { isSharedEndpoint, isTrainingJob }),
      start: cappedStart.toISOString(),
      end: end.toISOString(),
      stepSeconds: getStepSecondsForTimeRange([cappedStart, end]),
      timeDivisorSeconds:
        timeDivisorSeconds || getTimeDivisorSecondsForTimeRange([cappedStart, end]),
    };
  }, [end, cappedStart, timeDivisorSeconds, id, isSharedEndpoint, isTrainingJob]);
}

function useRegisterLoadingNewRelativeTimeRange(
  isLoadingNewRelativeTimeRange: boolean,
  isVisible: boolean,
) {
  const setCount = useSetRecoilState(loadingNewRelativeTimeRangeCountState);

  useEffect(() => {
    if (isLoadingNewRelativeTimeRange && isVisible) {
      setCount((prev) => prev + 1);
      return () => setCount((prev) => prev - 1);
    }
  }, [isLoadingNewRelativeTimeRange, setCount, isVisible]);
}

/**
 * Wrapper on useQuery providing fallback to previous values when the query is loading or errored. It also
 * provides the input variables and relative time range corresponding to the returned data so that any donwstream render
 * logic based off those inputs is consistent with the data.
 */
export function useMetricQuery<T>({
  document,
  isVisible = true,
  isSharedEndpoint,
  isTrainingJob,
  providerId,
  timeDivisorSeconds,
}: UseMetricQueryArgs<T>): MetricQueryHookAPI<T> {
  const relativeTimeRange = useRecoilValue(relativeTimeRangeState);
  const entityId = useEntityId();

  const variables = useQueryVariables({
    id: providerId || entityId,
    isSharedEndpoint,
    isTrainingJob,
    timeDivisorSeconds,
  });

  const { data, loading, error } = useQuery<T>(document, {
    variables,
    fetchPolicy: 'cache-first',
    skip: !isVisible,
  });

  const [latestValues] = useSyncState<
    Pick<MetricQueryHookAPI<T>, 'data' | 'inputVariables' | 'inputRelativeTimeRange'>
  >((prev) =>
    data || !prev
      ? {
          data,
          inputVariables: variables,
          inputRelativeTimeRange: relativeTimeRange,
        }
      : prev,
  );

  const isLoadingNewRelativeTimeRange = latestValues.inputRelativeTimeRange !== relativeTimeRange;

  useRegisterLoadingNewRelativeTimeRange(isLoadingNewRelativeTimeRange, isVisible);

  return {
    ...latestValues,
    loading: loading && !latestValues.data,
    error,
  };
}

export function useDateTimeRange(
  defaultDateRange?: DateRange<Moment>,
): [DateRange<Moment> | null, SetterOrUpdater<DateRange<Moment>>] {
  const [dateRange, setDateRange] = useRecoilState(timeRangeState);

  const setter = useCallback(
    (selectedDateRange: DateRange<Moment>) => {
      setDateRange(selectedDateRange ? convertToDate(selectedDateRange) : null);
    },
    [setDateRange],
  );

  useMount(() => {
    if (defaultDateRange) {
      delay(() => {
        setter(defaultDateRange);
      }, 0);
    }
  });

  return [dateRange ? convertToMoment(dateRange) : null, setter];
}

export function useMetricsConfig(
  showGpuCharts: boolean,
  transportKind: TransportKind,
  speculativeDecodingMetricsEnabled: boolean,
) {
  const { modelId, chainId, sharedEndpointId, trainingJobId } = useParams<MatchParams>();

  const [metricsConfig, setMetricsConfig] = useRecoilState(
    sharedEndpointId
      ? sharedEndpointMetricsConfigState(sharedEndpointId)
      : trainingJobId
      ? trainingJobMetricsConfigState(trainingJobId)
      : metricsConfigState(modelId || chainId),
  );

  const isWebsocketGrpcMetricsEnabled = useWebsocketGrpcMetricsEnabled();

  const availableMetricsConfig = getAvailableMetricsConfig({
    config: metricsConfig,
    showGpuCharts,
    transportKind,
    shouldFilterOutNonHttpTransportMetrics:
      !sharedEndpointId && !trainingJobId && isWebsocketGrpcMetricsEnabled,
    speculativeDecodingMetricsEnabled,
  });
  const validatedMetricsConfig = validateMetricsConfig(availableMetricsConfig);

  return [validatedMetricsConfig, setMetricsConfig] as const;
}

export function useSelectedMetricsUnits(chartId: string) {
  const { tabEntityId } = useParams<MatchParams>();

  const [selectedMetricsUnits, setSelectedMetricsUnits] = useRecoilState(
    selectedMetricsUnitsState(`${tabEntityId}-${chartId}`),
  );

  const toggleHeaderUnit = useCallback(
    (unit: string) => {
      setSelectedMetricsUnits((prev) => {
        if (prev.header.includes(unit)) {
          return { ...prev, header: prev.header.filter((u) => u !== unit) };
        }

        return { ...prev, header: [...prev.header, unit] };
      });
    },
    [setSelectedMetricsUnits],
  );

  const toggleLegendsUnit = useCallback(
    (unit: string) => {
      setSelectedMetricsUnits((prev) => {
        if (prev.legends.includes(unit)) {
          return { ...prev, legends: prev.legends.filter((u) => u !== unit) };
        }

        return { ...prev, legends: [...prev.legends, unit] };
      });
    },
    [setSelectedMetricsUnits],
  );

  function setHoveredLegends(units: string[]) {
    setSelectedMetricsUnits((prev) => ({ ...prev, hoveredLegends: units }));
  }

  return {
    selectedMetricsUnits,
    toggleHeaderUnit,
    toggleLegendsUnit,
    setHoveredLegends,
    setSelectedMetricsUnits,
  };
}
